import React from 'react';
import { TranslateFunc } from '@prisjakt/ui-translate/src/translate-func';
import { ClientMarket } from '@prisjakt/web-app-config/types/client-market';
import { PriceDisplay } from 'src/components/price-list/types';
import { Maybe, OfferPricePrice, OfferPrices, PricePrice } from 'src/types';
import { getDisplayPrice } from '../price-display';
import Currency from '@prisjakt/ui-currency';

export function getCompiledPriceData(
  price: PricePrice | null | undefined,
  offerPrice: OfferPrices | null | undefined,
  priceDisplay: PriceDisplay,
  featured: boolean,
  t: TranslateFunc,
  market: ClientMarket
) {
  if (!price) {
    return { label: null, mainPrice: null, secondaryPrice: null };
  }

  const isMemberPrice = getIsMemberPrice(price, offerPrice, featured);

  const isPromotion = getIsPromotion(price, offerPrice, featured);

  const secondaryPrice = getSecondaryPrice(
    offerPrice,
    priceDisplay,
    isPromotion,
    isMemberPrice
  );

  const hasPriceDate =
    featured &&
    isPromotion &&
    Boolean(secondaryPrice) &&
    Boolean(offerPrice?.price?.endDate);

  const label = getLabel(
    offerPrice,
    priceDisplay,
    hasPriceDate,
    isMemberPrice,
    featured,
    t,
    market
  );

  return {
    label,
    mainPrice: getDisplayPrice(price, priceDisplay),
    secondaryPrice,
    isMemberPrice,
    isPromotion,
  };
}

export function getIsPromotion(
  price: PricePrice | null | undefined,
  offerPrice: OfferPrices | null | undefined,
  featured: boolean
) {
  return featured && pricesAreEqual(price, offerPrice)
    ? offerPrice?.price?.exclShipping !==
        offerPrice?.originalPrice?.exclShipping
    : false;
}

export function getIsMemberPrice(
  price: PricePrice | null | undefined,
  offerPrice: OfferPrices | null | undefined,
  featured: boolean
): boolean {
  return featured && pricesAreEqual(price, offerPrice)
    ? Boolean(offerPrice?.memberPrice)
    : false;
}

export function pricesAreEqual(
  price: PricePrice | null | undefined,
  offerPrice: OfferPrices | null | undefined
): boolean {
  return price?.exclShipping === offerPrice?.price?.exclShipping;
}

export function getLabel(
  offerPrice: OfferPrices | null | undefined,
  priceDisplay: PriceDisplay,
  hasPriceDate: boolean,
  isMemberPrice: boolean,
  featured: boolean,
  t: TranslateFunc,
  market: ClientMarket
) {
  const promotionLabel =
    hasPriceDate && featured
      ? getPromotionLabel(offerPrice, market.localeStandard, t)
      : null;

  const memberLabel =
    isMemberPrice && featured
      ? getMemberLabel(offerPrice?.memberPrice, priceDisplay, t)
      : null;

  return memberLabel || promotionLabel;
}

export function getSecondaryPrice(
  offerPrice: OfferPrices | null | undefined,
  priceDisplay: PriceDisplay,
  isPromotion: boolean,
  isMemberPrice: boolean
): number | null {
  return offerPrice?.originalPrice && isPromotion && !isMemberPrice
    ? getDisplayPrice(offerPrice.originalPrice, priceDisplay)
    : null;
}

export function getPromotionLabel(
  offerPrice: OfferPrices | null | undefined,
  locale: string,
  t: TranslateFunc
) {
  if (!offerPrice?.price?.endDate) {
    return null;
  }
  return `${t('product', 'promotions.exp-date-label')} ${parseUTCDate(
    offerPrice!.price!.endDate!,
    locale
  )}`;
}

export function parseUTCDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  const timeDiff = date.getTimezoneOffset() * 60000;
  const adjustedUTCDate = new Date(date.valueOf() + timeDiff);
  return adjustedUTCDate.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
  });
}

export function getMemberLabel(
  price: Maybe<OfferPricePrice> | undefined,
  priceDisplay: PriceDisplay,
  t: TranslateFunc
) {
  const p = getDisplayPrice(price, priceDisplay);

  return (
    <>
      {t('product', 'promotions.members-price')} <Currency price={p} />
    </>
  );
}
